import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useCookieConsent } from "@/contexts/CookieConsentContext";

export function CookieConsentBanner() {
  const { consent, isOpen, acceptAll, rejectAll, updateConsent, openPreferences, closePreferences } = useCookieConsent();
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAnalytics(consent?.analytics ?? false);
      setMarketing(consent?.marketing ?? false);
    }
  }, [isOpen, consent]);

  const showBanner = consent === null;

  return (
    <>
      {showBanner && (
        <div
          role="dialog"
          aria-label="Cookie consent"
          className="fixed bottom-0 inset-x-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-lg"
        >
          <div className="container mx-auto px-4 py-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-foreground max-w-3xl">
              We use cookies to run our site, analyze usage, and improve your experience. You can accept all,
              reject non-essential, or customize. See our{" "}
              <Link to="/cookie-policy" className="underline text-primary">Cookie Policy</Link>.{" "}
              California residents may use{" "}
              <button onClick={rejectAll} className="underline text-primary">
                Do Not Sell or Share My Personal Information
              </button>{" "}
              (or visit the{" "}
              <Link to="/do-not-sell" className="underline text-primary">full opt-out page</Link>).
            </p>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button variant="outline" onClick={rejectAll}>Reject non-essential</Button>
              <Button variant="outline" onClick={openPreferences}>Customize</Button>
              <Button onClick={acceptAll}>Accept all</Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={isOpen} onOpenChange={(o) => (o ? openPreferences() : closePreferences())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription>
              Choose which categories of cookies you allow. Essential cookies are required for the site to work.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">Essential</p>
                <p className="text-sm text-muted-foreground">Required for authentication, security, and core features.</p>
              </div>
              <Switch checked disabled aria-label="Essential cookies are always on" />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">Analytics</p>
                <p className="text-sm text-muted-foreground">Helps us understand how the site is used so we can improve it.</p>
              </div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} aria-label="Toggle analytics cookies" />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">Marketing</p>
                <p className="text-sm text-muted-foreground">Used to personalize and measure marketing campaigns.</p>
              </div>
              <Switch checked={marketing} onCheckedChange={setMarketing} aria-label="Toggle marketing cookies" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={rejectAll}>Reject non-essential</Button>
            <Button onClick={() => updateConsent({ analytics, marketing })}>Save preferences</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function CookiePreferencesLink({ className }: { className?: string }) {
  const { openPreferences } = useCookieConsent();
  return (
    <button onClick={openPreferences} className={className}>
      Cookie preferences
    </button>
  );
}
